import React, { useState, useEffect } from 'react';
import ColaboradorLayout from '../../components/ColaboradorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { notificationsAPI } from '../../services/api';
import { Bell, Check, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      loadNotifications();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      toast.success('Todas las notificaciones marcadas como leídas');
      loadNotifications();
    } catch (error) {
      toast.error('Error');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <ColaboradorLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Notificaciones
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todas leídas'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              onClick={handleMarkAllRead}
              className="rounded-sm"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Marcar todas como leídas
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : notifications.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Bell className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No tienes notificaciones</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="space-y-3">
            {notifications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((notification) => (
              <Card 
                key={notification.id} 
                className={`border transition-colors ${
                  notification.read 
                    ? 'border-slate-200 bg-white' 
                    : 'border-blue-200 bg-blue-50'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${notification.read ? 'bg-slate-300' : 'bg-[#002FA7]'}`} />
                      <div>
                        <h4 className="font-medium">{notification.title}</h4>
                        <p className="text-sm text-slate-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {notification.created_at ? new Date(notification.created_at).toLocaleString('es-CL', { timeZone: 'America/Santiago', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkRead(notification.id)}
                        className="text-slate-400 hover:text-[#002FA7]"
                        data-testid={`mark-read-${notification.id}`}
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {Math.ceil(notifications.length / PAGE_SIZE) > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 pt-4" data-testid="notif-pagination">
              <p className="text-sm text-slate-500">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, notifications.length)} de {notifications.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.ceil(notifications.length / PAGE_SIZE) }, (_, i) => i + 1).map((p) => (
                  <Button
                    key={p}
                    variant={p === page ? 'default' : 'outline'}
                    size="icon"
                    className={`h-8 w-8 rounded-sm text-xs ${p === page ? 'bg-[#002FA7] hover:bg-[#001B66]' : ''}`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </Button>
                ))}
                <Button variant="outline" size="icon" className="h-8 w-8 rounded-sm" disabled={page === Math.ceil(notifications.length / PAGE_SIZE)} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
        )}
      </div>
    </ColaboradorLayout>
  );
};

export default NotificationsPage;
